/**
 * What Partnr's web renderers do with a result: put it on the board as a
 * link card drawn from what the search already had (no wait for a preview),
 * several at once into a folder named after the search, or at a dropped
 * point. Plus finding the cards a receipt points at, for Show.
 */
import { useMemo } from 'react';
import { executeCanvasAction } from '@/lib/partnrActionExecutor';
import { useCanvasStore, type Item } from '@/stores/canvasStore';
import { newSourcesOnly, plainSnippet, sourceKeysOf, sourceTitle } from './sourceDisplay';

export { newSourcesOnly };
import type { PartnrCanvasAction, WebSource } from './types';

/** More results than this go into a new folder named after the search. */
export const ADD_ALL_FOLDER_AFTER = 4;
/** The drag payload a result card carries onto the canvas. */
export const PARTNR_SOURCE_DRAG_TYPE = 'partnr_source';

/** The add_links preview for one result, as the model would have copied it. */
export function previewOf(source: WebSource): Record<string, string> {
  const preview: Record<string, string | null | undefined> = {
    title: sourceTitle(source),
    description: plainSnippet(source.highlights?.[0] || source.snippet),
    image: source.image,
    site: source.site,
    favicon: source.favicon,
  };
  return Object.fromEntries(Object.entries(preview).filter((e): e is [string, string] => typeof e[1] === 'string' && !!e[1]));
}

/** "nordic interior trends 2027" -> "Nordic interior trends 2027", short enough for a folder. */
export function folderNameFor(query: string): string {
  const text = query.trim().replace(/\s+/g, ' ').slice(0, 60);
  return text ? text[0].toUpperCase() + text.slice(1) : 'Web sources';
}

/** Keys (see sourceKey) of the links already on the open board. */
export function boardSourceKeys(items: Item[] = useCanvasStore.getState().items): Set<string> {
  return sourceKeysOf(items);
}

// One scan per items array, not per store change (drags change the store every frame)
const keysByItems = new WeakMap<Item[], string>();

/**
 * Keys of the links on the open board, for marking source cards as added.
 * Re-renders only when that set changes.
 */
export function useBoardSourceKeys(): Set<string> {
  const joined = useCanvasStore((s) => {
    let cached = keysByItems.get(s.items);
    if (cached === undefined) {
      cached = [...sourceKeysOf(s.items)].sort().join('\n');
      keysByItems.set(s.items, cached);
    }
    return cached;
  });
  return useMemo(() => new Set(joined ? joined.split('\n') : []), [joined]);
}

/**
 * Adds results as link cards; more than four new ones go into a folder named
 * after the query. Links already on the board, or twice in the batch, are
 * skipped, and the executor's toast says how many ("Added 3, 1 was already
 * here").
 */
export async function addSourcesToBoard(sources: WebSource[], query = ''): Promise<void> {
  if (sources.length === 0 || !useCanvasStore.getState().currentBoardId) return;
  const previews = Object.fromEntries(sources.map((s) => [s.url, previewOf(s)]));
  const { fresh, skipped } = newSourcesOnly(sources, (s) => s.url, boardSourceKeys());
  if (fresh.length > ADD_ALL_FOLDER_AFTER) {
    // The folder is new, so the executor cannot see what was left out: it is told
    const folderId = crypto.randomUUID();
    await executeCanvasAction({ action: 'create_folder', payload: { folder_id: folderId, name: folderNameFor(query), item_ids: [] } });
    await executeCanvasAction({ action: 'add_links', payload: { urls: fresh.map((s) => s.url), previews, board_id: folderId, skipped } });
    return;
  }
  // On the open board the executor skips what is already there and says so
  await executeCanvasAction({ action: 'add_links', payload: { urls: sources.map((s) => s.url), previews } });
}

/** Plain URLs (older source cards) as link cards on the open board; ones already there are skipped. */
export async function addUrlsToBoard(urls: string[]): Promise<void> {
  if (urls.length === 0) return;
  await executeCanvasAction({ action: 'add_links', payload: { urls, board_id: null } });
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** Cards on the current board a receipt's action made, matched by what they hold. */
export function findItemsForAction(action: PartnrCanvasAction | null | undefined): string[] {
  if (!action) return [];
  const items = useCanvasStore.getState().items.filter((i) => !i.isGhost);
  const content = (i: (typeof items)[number]) => (i.content ?? {}) as Record<string, unknown>;
  const p = action.payload;
  switch (action.action) {
    case 'add_images': {
      const urls = new Set((Array.isArray(p.images) ? p.images : []).map((img) => str((img as { url?: unknown }).url)));
      return items.filter((i) => i.item_type === 'image' && urls.has(str(content(i).url))).map((i) => i.id);
    }
    case 'clip_to_board':
      return items
        .filter((i) => i.item_type === 'text_card' && str(content(i).source_url) === str(p.source_url) && str(content(i).body).includes(str(p.quote).slice(0, 40).replace(/</g, '&lt;')))
        .map((i) => i.id);
    case 'create_document':
      return items.filter((i) => i.item_type === 'document' && content(i).partnr_generated === true && str(content(i).title) === str(p.title)).map((i) => i.id);
    case 'add_links': {
      const urls = new Set(Array.isArray(p.urls) ? (p.urls as string[]) : []);
      return items.filter((i) => i.item_type === 'link' && urls.has(str(content(i).url))).map((i) => i.id);
    }
    default:
      return [];
  }
}
